// Import required libraries
import React, { useContext } from "react";
import PropTypes from "prop-types";
import orderBy from "lodash.orderby";
import { Box } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";

// Import custom components
import SigviewTextField from "../../components/Common/SigviewTextField";
import SigviewRadioGroup from "../../components/Common/SigviewRadioGroup";
import SigviewSingleSelect from "../../components/Common/SigviewSingleSelect";
import SigviewButtonGroup from "../../components/Common/SigviewButtonGroup";
import SigviewCheckboxGroup from "../../components/Common/SigviewCheckboxGroup";
import SigviewSimpleText from "../../components/Common/SigviewSimpleText";
import SigviewRadioDropdownCombo from "../../components/Common/SigviewRadioDropdownCombo";
import SigviewInputTags from "../../components/Common/SigviewInputTags";
import SigviewMultiSelectDnD from "../../components/Common/SigviewMultiSelectDnD";
import SelectedFilters from "../../components/GlobalFilters/SelectedFilters";
import TimeFilters from "../../components/TimeFilters/TimeFilters";
import SigviewButton from "../../components/Common/SigviewButton";
import SigviewSwitch from "../../components/Common/SigviewSwitch";
import SigviewTooltip from "../../components/Common/SigviewTooltip";
import SigviewComboBox from "../../components/Common/SigviewComboBox";
import Loader from "../../components/Loader/Loader";
import AnalyzeShareDialog from "../../screens/Analyze/AnalyzeDashboard/AnalyzeShareDialog";

// Import styles
import "./NewReportManager.scss";

// Import config

// Import action creators and reducers
import { updateUserScreen } from "../../redux/actions";

// Import utils
import { validateEmailAddress, getSigviewUserType } from "../../utils/utils";
import { masterTrackGaEvent } from "../../services/ga";
import {
  realTimeCprFrequencyFilter,
  realTimeCprTimeRange,
  isBucketAssociatedReport,
  wrapperScheduledReport,
  wrapperSavedReport,
} from "../../utils/reportUtils";

// Import data
import allTimezones from "../../../assets/data/allTimezones.json";
import triggerFrequencyArr from "../../../assets/data/triggerFrequencyArr.json";
import allWeekdays from "../../../assets/data/allWeekdays.json";
import triggerTimeArr from "../../../assets/data/triggerTimeArr.json";
import { ThemeContext } from "../../contexts/ThemeContext";
import SigviewTypography from "../../components/Common/SigviewTypography";
import { connect } from "react-redux";

const reportTypesArr = [
  { id: "no", name: "Saved", disabled: false },
  { id: "yes", name: "Scheduled", disabled: false },
  { id: "no", name: "Download", disabled: false },
];

const languageOptionsArr = [
  {
    id: "en",
    name: "English",
    disabled: false,
  },
  {
    id: "ja",
    name: "Japanese",
    disabled: false,
  },
];

const ascDescArr = [
  { id: "asc", name: "Ascending" },
  { id: "desc", name: "Descending" },
];

const sortedByTypeArr = [
  {
    id: "id_only",
    name: "Value",
    idStartsWithList: ["D", "C", "M"],
  },
  {
    id: "id_trueDelta",
    name: "Absolute Change",
    idStartsWithList: ["C", "M"],
  },
  {
    id: "id_deltaPercentage",
    name: "Percentage Change",
    idStartsWithList: ["C", "M"],
  },
];

const asteriskStyle = { color: "red" };

function NewReportManagerForm(props) {
  const { t } = useTranslation();
  const {
    user,
    reportFormData,
    setReportFormDataSync,
    allSelectedMetricsDimensions,
    filteredTimeRangeScheduledArr,
    ReduxDispatcher,
    allData,
    reportFormError,
    activeReportFormData,
    activeReport,
    buttonParams,
    uiFeatureList,
    disabledAddNewReport,
    bucketList = [],
    variant = "report",
    usersData,
  } = props;

  const pathname = window.location.pathname;
  const rootPage = pathname.split("/")[1];
  //Defining Variables
  const sigviewUserType = getSigviewUserType();
  const isLanguageSelectionAvailable = sigviewUserType !== "sigview"; //hardCoded
  const isQuarterlyVisible = sigviewUserType === "sigview";
  const allTimezonesFinal = allTimezones.map((row) => ({
    ...row,
    id: row.name,
    name: row.name,
    disabled: false,
  }));

  //Define required hooks
  const { state: themeState } = useContext(ThemeContext);
  const themeColors = themeState.themes[themeState.activeTheme];

  // If view is REAL-TIME-CPR frequency shuld be only daily or weekly
  let ModifiedTriggerFrequencyArr = triggerFrequencyArr;
  if (user.reqMetadata.view === "REAL-TIME-CPR") {
    ModifiedTriggerFrequencyArr = triggerFrequencyArr.filter((el) =>
      realTimeCprFrequencyFilter.includes(el.id)
    );
  }
  // Quarterly will be visible only for openx users and not for sigview users
  if (isQuarterlyVisible) {
    ModifiedTriggerFrequencyArr = triggerFrequencyArr.filter(
      (el) => el.id !== "quarterly"
    );
  }

  //If value is not present in the list, don't show the component to avoid MUI warning
  if (
    user.reqMetadata.organization === "OpenX" &&
    user.reqMetadata.view === "REAL-TIME-CPR"
  ) {
    var realTimeCPRTimeRangeScheduledArr = filteredTimeRangeScheduledArr.filter(
      (el) => realTimeCprTimeRange.includes(el.id)
    );
    var selectedTimeRange = realTimeCPRTimeRangeScheduledArr.find(
      (row) => row.id === reportFormData.triggerTimeRangeNumber
    );
  } else {
    var selectedTimeRange = filteredTimeRangeScheduledArr.find(
      (row) => row.id === reportFormData.triggerTimeRangeNumber
    );
  }
  const timeRangeScheduledReportOnComparisonText =
    selectedTimeRange?.compareWithPresetName;
  const isComparisonAvailable =
    selectedTimeRange?.isComparisonAvailable &&
    reportFormData.reportRowCount !== "all_records"; //hard coded
  const isComparisonOn = reportFormData.isComparisonOn;
  const timeRangeScheduledReportOnComparisonTextFlag =
    timeRangeScheduledReportOnComparisonText?.length && isComparisonOn
      ? true
      : false;
  const triggerTypeCurrObject = triggerFrequencyArr.find(
    (row) => row.id === reportFormData.triggerType
  );
  const triggerTypeHelperText = triggerTypeCurrObject.helperText;
  const weeklyRadioButtonFlag = reportFormData.triggerType === "weekly"; //hard coded
  const triggerTypeHelperTextFlag = ["weekly", "daily"].includes(
    reportFormData.triggerType
  )
    ? false
    : true; //hard coded
  const orderByShowFlag = allSelectedMetricsDimensions.length ? true : false;
  const selectedReportType =
    reportFormData.scheduleFlag === "yes" ? "scheduled" : "saved";
  let filteredReportCountFileType = reportFormData.reportCountFileType
    .filter((row) => row.reportTypeList.includes(selectedReportType))
    .filter((row) =>
      row.id === "all_records" ? uiFeatureList.fullReport : row
    )
    .filter((row) =>
      row.id === "five_thousand_records" ? uiFeatureList.download : row
    );
  //Hard coded: if userType is sigview, all_records (full report) is not available
  if (sigviewUserType === "sigview" && selectedReportType === "scheduled") {
    filteredReportCountFileType = filteredReportCountFileType.filter(
      (row) => row.id !== "all_records"
    );
  }
  if (sigviewUserType === "nonSigview" && selectedReportType === "scheduled") {
    filteredReportCountFileType = filteredReportCountFileType.map((el) =>
      el.id === "fifty_thousand_records"
        ? {
            ...el,
            dropDownData: el.dropDownData.some(
              (el) => el.id === "writeToBucket"
            )
              ? [...el.dropDownData]
              : [
                  ...el.dropDownData,
                  {
                    id: "sendLink",
                    name: "Send as a link",
                    disabled: false,
                  },
                  {
                    id: "writeToBucket",
                    name: "Write to storage",
                    disabled: false,
                  },
                ],
          }
        : el
    );
  }
  const emailRecordVolTypes = ["fifty_thousand_records", "all_records"];
  const showEmailComponent =
    selectedReportType === "saved" &&
    emailRecordVolTypes.includes(reportFormData.reportRowCount);
  const sortedByTypeArrFiltered = sortedByTypeArr
    .filter((row) => {
      if (reportFormData.orderById.length)
        return row.idStartsWithList.includes(
          reportFormData.orderById.slice(0, 1)
        );
      else return true;
    })
    .filter((row) => {
      if (isComparisonOn) {
        return true;
      } else {
        if (row.id === "id_only") return true;
        else return false;
      }
    });
  const calendarDaysLimits = user.uiLimitsList.daysLimitCalendarReports;
  const timeFiltersSettings = {
    compareCalendarRowLimit: 1,
    isMultiCompareRowAllowed: false,
    isComparisonAvailable: reportFormData.reportRowCount !== "all_records",
    isChangeTypeDropdownAvailable: false,
    setHeight: "33px",
    disabled: false,
  };
  const calendarComparisonData = [
    {
      id: "compareWith",
      name: "Compare With",
      checked: reportFormData.isComparisonOn,
    },
  ];
  //If value is not present in the list, don't show the component to avoid MUI warning
  const orderByIdValid = allSelectedMetricsDimensions.find(
    (row) => row.id === reportFormData.orderById
  );
  const orderByTypeValid = sortedByTypeArrFiltered.find(
    (row) => row.id === reportFormData.orderByType
  );
  const metricListErrorStyle = {
    display: reportFormError.message.metricsList.flag ? "block" : "none",
    //background: "#f5f7ff",
  };
  const dimensionListErrorStyle = {
    display: reportFormError.message.dimensionsList.flag ? "block" : "none",
  };
  const isFullReportOn = reportFormData.reportRowCount === "all_records"; //hard coded
  const atleastOneFilterSelected = reportFormData.dimensionFilters.length > 0;

  // Required for DnD component
  // Dimensions
  const dimensionIdAccessor = "_id";
  const dimensionLabelAccessor = "dimTitle";
  const selectedDimensionsIds = reportFormData.dimensionsList.map(
    (row) => row[dimensionIdAccessor]
  );
  const unselectedDimensions = allData.plotlyDimensions
    .filter((row) => !selectedDimensionsIds.includes(row[dimensionIdAccessor]))
    .filter((row) => row[dimensionLabelAccessor].toLowerCase() !== "week"); //hard coded: remove week from the list
  const dimensionsDndData = [
    // NOT ORDERDERING THE SELECTED DIMENSIONS; They will in the order they are selected
    // ...orderBy(
    //   reportFormData.dimensionsList,
    //   [dimensionLabelAccessor],
    //   ["asc"]
    // ),
    ...reportFormData.dimensionsList,
    //HARD CODED: Placing timestamp dimensions first
    ...orderBy(
      unselectedDimensions.filter(
        (row) => row.dataType.toLowerCase() === "datetime"
      ),
      [dimensionLabelAccessor],
      ["asc"]
    ),
    //HARD CODED: Placing non-timestamp dimensions later
    ...orderBy(
      unselectedDimensions.filter(
        (row) => row.dataType.toLowerCase() === "string"
      ),
      [dimensionLabelAccessor],
      ["asc"]
    ),
  ];
  // Metrics
  const metricIdAccessor = "_id";
  const metricLabelAccessor = "measureTitle";
  const selectedMetricsIds = reportFormData.metricsList.map(
    (row) => row[metricIdAccessor]
  );
  const unselectedMetrics = allData.plotlyMetrics.filter(
    (row) => !selectedMetricsIds.includes(row[metricIdAccessor])
  );
  const metricsDndData = [
    // NOT ORDERDERING THE SELECTED DIMENSIONS; They will in the order they are selected
    ...reportFormData.metricsList,
    //HARD CODED: Placing timestamp dimensions first
    ...orderBy(unselectedMetrics, [metricLabelAccessor], ["asc"]),
  ];

  let reportTypesArrFinal = [...reportTypesArr];
  let reportTypeName = undefined;
  if (activeReportFormData.reportPayload) {
    reportTypeName = `${activeReport.type}`;
    if (activeReport.type === "download") {
      reportTypesArrFinal = reportTypesArr
        .filter((row) => row.name === "Download")
        .map((row) => ({ ...row, disabled: true }));
    } else {
      reportTypesArrFinal = reportTypesArr
        .filter((row) => row.name !== "Download")
        .map((row) => ({ ...row, disabled: true }));
    }
  } else {
    reportTypesArrFinal = reportTypesArr.filter(
      (row) => row.name !== "Download"
    );
  }
  const dimensionFiltersSettings = {
    isAdvancedFilterVisible: true,
    isExcludeAllowed: true,
    disabledItemsList: [], // ["D044"] --> This needs to be a array of ids
  };

  //Defining change handlers

  const handleAddFilters = () => {
    const handleApplyFilters = (payload, filterType) => {
      switch (filterType) {
        case "dimensions":
          setReportFormDataSync(
            "dimensionFilters",
            payload.newDimensionFilters
          );
          break;
        case "metrics":
          setReportFormDataSync("metricFilters", payload.newMetricFilters);
          break;
        case "time":
          setReportFormDataSync("timeFilters", payload);
          break;
      }
    };

    // Google Analytics Event - Master
    masterTrackGaEvent({
      category: "GlobalFilters",
      action: "Open Filters",
      label: "Sidenav",
    });
    const commonGlobalFilterProps = {
      isOpen: true, //global filters dialog open close
      showTimeFilters: false,
      showMetricFilters: uiFeatureList.metricFilters,
      activeFilterType: "dimensions",
      isAdFiltersOpen: false, //advanced filters dialog open close
      timeFilters: reportFormData.timeFilters, //for initializing filters
      dimensionFilters: reportFormData.dimensionFilters, //for initializing filters
      metricFilters: reportFormData.metricFilters, //for initializing filters
      settings: dimensionFiltersSettings,
      filtersDimData: allData.plotlyDimensions,
      filtersMetricsData: allData.plotlyMetrics,
      selections: {},
    };
    //Open Global Filters
    const newGlobalFiltersProps = {
      ...commonGlobalFilterProps,
      activeDimensionFilter: {},
      handleApplyFilters, //It will take 2 parameters, payload and filterType (dimensions or metric or time); We have 2 separate actions for both
    };

    const action = updateUserScreen("globalFilters", newGlobalFiltersProps);
    ReduxDispatcher(action);
  };

  const handleDimFilterClick = (filterRow) => {
    //Open Global Filters
    const newGlobalFiltersProps = {
      isOpen: true, //global filters dialog open close
      showTimeFilters: false,
      showMetricFilters: uiFeatureList.metricFilters,
      activeFilterType: "dimensions",
      isAdFiltersOpen: false, //advanced filters dialog open close
      activeDimensionFilter: filterRow.metadata,
      timeFilters: reportFormData.timeFilters, //for initializing filters
      dimensionFilters: reportFormData.dimensionFilters, //for initializing filters
      metricFilters: reportFormData.metricFilters, //for initializing filters
      handleApplyFilters: (payload, filterType) => {
        switch (filterType) {
          case "dimensions":
            setReportFormDataSync(
              "dimensionFilters",
              payload.newDimensionFilters
            );
            break;
          case "metrics":
            setReportFormDataSync("metricFilters", payload.newMetricFilters);
            break;
          case "time":
            setReportFormDataSync("timeFilters", payload);
            break;
        }
      }, //It will take 2 parameters, payload and filterType (dimensions or metric or time); We have 2 separate actions for both
    };
    ReduxDispatcher(updateUserScreen("globalFilters", newGlobalFiltersProps));
  };

  const handleDimFilterDelete = (filterRow) => {
    const newDimFilters = reportFormData.dimensionFilters.filter(
      (filter) => filter.id !== filterRow.id
    );
    setReportFormDataSync("dimensionFilters", newDimFilters);
  };

  const handleMetricFilterClick = (metricRow) => {
    //Open Global Filters
    const newGlobalFiltersProps = {
      isOpen: true, //global filters dialog open close
      showTimeFilters: false,
      showMetricFilters: uiFeatureList.metricFilters,
      activeFilterType: "metrics",
      isAdFiltersOpen: false, //advanced filters dialog open close
      activeDimensionFilter: {},
      timeFilters: reportFormData.timeFilters, //for initializing filters
      dimensionFilters: reportFormData.dimensionFilters, //for initializing filters
      metricFilters: reportFormData.metricFilters, //for initializing filters
      handleApplyFilters: (payload, filterType) => {
        switch (filterType) {
          case "dimensions":
            setReportFormDataSync(
              "dimensionFilters",
              payload.newDimensionFilters
            );
            break;
          case "metrics":
            setReportFormDataSync("metricFilters", payload.newMetricFilters);
            break;
          case "time":
            setReportFormDataSync("timeFilters", payload);
            break;
        }
      }, //It will take 2 parameters, payload and filterType (dimensions or metric or time); We have 2 separate actions for both
    };
    ReduxDispatcher(updateUserScreen("globalFilters", newGlobalFiltersProps));
  };

  const handleMetricFilterDelete = (metricRow) => {
    const newDimFilters = reportFormData.metricFilters.filter(
      (filter) => filter.id !== metricRow.id
    );
    setReportFormDataSync("metricFilters", newDimFilters);
  };

  const handleMetricsListChange = (newValue) => {
    setReportFormDataSync("metricsList", newValue);
  };

  const handleDimensionsListChange = (newValue) => {
    setReportFormDataSync("dimensionsList", newValue);
  };

  const handleClearAllFilters = () => {
    setReportFormDataSync(undefined, {
      dimensionFilters: [],
      metricFilters: [],
    });
  };

  const validateEmail = (text) => {
    const recipientsList = reportFormData.recipientsList.map((row) => row.name);
    if (!validateEmailAddress(text))
      return { isValid: false, message: "Invalid email address" };
    if (recipientsList.includes(text))
      return { isValid: false, message: "Duplicate email address" };
    return { isValid: true, message: "" };
  };

  const renderOptionAutoCompleteMetric = (props) => {
    const { option, allSelectedIds, idAccessor, labelAccessor } = props;
    const selectedFlag = allSelectedIds.includes(option[idAccessor]);
    const metricType = option._id.startsWith("M") ? "metric" : "customMetric";
    let infoTooltipStatus = false;
    if (
      user.reqMetadata.organization === "Kayzen" &&
      user.reqMetadata.view === "Percentile"
    ) {
      infoTooltipStatus =
        (option.displayFormula !== undefined &&
          option.displayFormula.startsWith("(Price Digest")) ||
        (option.displayFormula !== undefined &&
          option.displayFormula.startsWith("(price digest"));
    }
    return (
      <>
        <i
          className={`material-icons auto-complete-option-icon ${
            selectedFlag ? "" : "invisible"
          } `}
        >
          check_circle
        </i>
        <span
          className="quantity-selector-option-title"
          title={option[labelAccessor]}
        >
          {option[labelAccessor]}
        </span>
        {!infoTooltipStatus && (
          <SigviewTooltip title={option.displayFormula} top="-12px">
            <i className="material-icons-round info-icon">info</i>
          </SigviewTooltip>
        )}
      </>
    );
  };

  const renderOptionAutoCompleteDimension = (props) => {
    const { option, allSelectedIds, idAccessor, labelAccessor } = props;
    const selectedFlag = allSelectedIds.includes(option[idAccessor]);
    let infoTooltipStatus = false;
    if (
      user.reqMetadata.organization === "Kayzen" &&
      user.reqMetadata.view === "Inventory_Price"
    ) {
      infoTooltipStatus =
        (option.displayFormula !== undefined &&
          option.displayFormula.startsWith("(Price Digest")) ||
        (option.displayFormula !== undefined &&
          option.displayFormula.startsWith("(price digest"));
    }
    return (
      <>
        <i
          className={`material-icons auto-complete-option-icon ${
            selectedFlag ? "" : "invisible"
          } `}
        >
          check_circle
        </i>
        <span
          className="quantity-selector-option-title"
          title={option[labelAccessor]}
        >
          {option[labelAccessor]}
        </span>
        {option.description && !infoTooltipStatus && (
          <SigviewTooltip title={option.description} top="-12px">
            <i className="material-icons-round info-icon">info</i>
          </SigviewTooltip>
        )}
      </>
    );
  };

  const allowTimeRangeForRealTimeCPR =
    selectedTimeRange !== undefined &&
    user.reqMetadata.organization === "OpenX" &&
    user.reqMetadata.view === "REAL-TIME-CPR";
  const allowAllTimeRangeForAllView =
    selectedTimeRange !== undefined &&
    user.reqMetadata.view !== "REAL-TIME-CPR";

  const handleShare = () => {
    let fetchPayload = {};

    if (reportFormData.scheduleFlag === "no") {
      const wrapperProps = {
        activeReportFormData: {
          reportFormData: { ...reportFormData, reportId: `report-${v4()}` }, //Updating Id
          reportPayload: {}, //Adding download/scheduled report to saved reports
        },
        user: user,
        allData: allData,
        activeReport,
        addSavedReportFlag: false,
      };
      const savedReportPayload = wrapperSavedReport(wrapperProps);
      const transformedPayload = {
        email: savedReportPayload.email,
        orgViewReq: savedReportPayload.orgViewReq,
        id: savedReportPayload.recentDownloadRecord.id,
        name: savedReportPayload.recentDownloadRecord.name,
        payload: savedReportPayload.recentDownloadRecord.payload,
        metricOrder: savedReportPayload.recentDownloadRecord.metricOrder,
        createdOn: savedReportPayload.recentDownloadRecord.createdOn,
        isComparison: savedReportPayload.recentDownloadRecord.isComparison,
        recipientsList: savedReportPayload.recentDownloadRecord.recipientsList,
        fileFormat: savedReportPayload.recentDownloadRecord.fileFormat,
        operationType: savedReportPayload.recentDownloadRecord.operationType,
        userPreferredLanguage:
          savedReportPayload.recentDownloadRecord.userPreferredLanguage,
      };
      fetchPayload = {
        variant: "saved",
        savedReportPayload: transformedPayload,
      };
    } else if (reportFormData.scheduleFlag === "yes") {
      //hard coded
      //If the saved report had five_thoudsand_records selected, API will fail, change it to fifty_thousand_records
      const reportRowCount = ["fifty_thousand_records", "all_records"].includes(
        reportFormData.reportRowCount
      )
        ? reportFormData.reportRowCount
        : "fifty_thousand_records";
      const wrapperProps = {
        activeReportFormData: {
          reportFormData: {
            ...reportFormData,
            reportId: `report-${v4()}`,
            reportRowCount,
          }, //Updating Id
          reportPayload: {}, //Adding download/saved report to scheduled reports
        },
        user: user,
        allData: allData,
        activeReport,
      };
      fetchPayload = {
        variant: "scheduled",
        scheduledReportPayload: wrapperScheduledReport(wrapperProps),
      };
    }
    const actionPayload = {
      open: true,
      children: (
        <AnalyzeShareDialog
          user={user}
          ReduxDispatcher={ReduxDispatcher}
          payload={fetchPayload}
          type="report"
        />
      ),
    };
    const action = updateUserScreen("sigviewDialog", actionPayload);
    ReduxDispatcher(action);
  };

  return (
    <>
      {!reportTypeName && (
        <div className="report-manager-form-row">
          <div className="report-manager-form-item-container">
            <div className="label-container">
              <p className="label">{t("Report Type:")}</p>
            </div>
          </div>
          <div className="input-item-container-RadioBtn">
            {reportTypeName ? (
              <p className="simple-text" style={{ fontSize: "12px" }}>
                {reportTypeName}
              </p>
            ) : (
              <SigviewRadioGroup
                data={reportTypesArrFinal}
                value={reportFormData.scheduleFlag}
                onChange={(newValue) => {
                  setReportFormDataSync("scheduleFlag", newValue);
                }}
                //   style={{ rootFlexDirection: "row" }}
                style={{
                  // backgroundColor: `${themeColors["primaryColor"]}1A`,
                  backgroundColor: "#EEEFF2",
                  borderRadius: "4px",
                  fontSize: "12px",
                  padding: "4.5px 10px 4.5px 6px",
                }}
                iconFlag={true}
                iconProps={{ className: "material-icons", iconName: "add" }}
              />
            )}
          </div>
        </div>
      )}
      <div className="report-manager-form-row">
        <div
          className={`report-manager-form-item-container report-name ${
            reportTypeName ? "clone-visible" : "full"
          }`}
        >
          <div className="label-container">
            <p className="label">{t("Name:")}</p>
          </div>
          <Box
            style={{
              display: "flex",
              gap: "8px",
              justifyContent: "flex-end",
              alignContent: "center",
            }}
          >
            {reportTypeName && buttonParams.showAddToSavedReportsButton && (
              // <div className="report-manager-form-item-container label-less">
              <div
                className={`input-item-container border-less label-less report-clone-menu-container`}
                id="reportFormCloneButton"
              >
                <SigviewTooltip
                  title={
                    disabledAddNewReport
                      ? "Max Limit Reached For Saved Reports"
                      : ""
                  }
                  placement="bottom"
                >
                  <Box>
                    <SigviewButton
                      variant="containedPrimary"
                      onClick={buttonParams.handleAddToSavedReport}
                      title="Add to Saved Reports"
                      customClassName={`${rootPage}AddToSavedReports-GA`}
                      disabled={
                        reportFormError.flag ||
                        disabledAddNewReport ||
                        isBucketAssociatedReport(reportFormData)
                      }
                      iconFlag={true}
                      iconProps={{
                        className: "material-icons-round",
                        iconName: "add_circle",
                      }}
                      iconStyle={{ fontSize: "13px", margin: "2px 0px" }}
                      style={{ padding: "4px 10px", buttonHeight: "22px" }}
                    />
                  </Box>
                </SigviewTooltip>
              </div>
              // </div>
            )}
            {reportTypeName && buttonParams.showAddToScheduledReportsButton && (
              // <div className="report-manager-form-item-container label-less">
              <div
                className={`input-item-container border-less label-less report-clone-menu-container`}
                id="reportFormCloneButton"
              >
                <SigviewTooltip
                  title={
                    disabledAddNewReport
                      ? "Max Limit Reached For Scheduled Reports"
                      : ""
                  }
                  placement="bottom"
                >
                  <Box>
                    <SigviewButton
                      variant="containedPrimary"
                      onClick={buttonParams.handleAddToScheduledReports}
                      title="Add to Scheduled Reports"
                      customClassName={`${rootPage}AddToScheduledReports-GA`}
                      disabled={reportFormError.flag || disabledAddNewReport}
                      iconFlag={true}
                      iconProps={{
                        className: "material-icons-round",
                        iconName: "add_circle",
                      }}
                      iconStyle={{ fontSize: "13px", margin: "2px 0px" }}
                      style={{ padding: "4px 10px", buttonHeight: "22px" }}
                    />
                  </Box>
                </SigviewTooltip>
              </div>
              //  </div>
            )}
            <SigviewButton
              variant="containedPrimary"
              onClick={handleShare}
              title="Share Report"
              customClassName="ShareReport-GA"
              iconFlag={true}
              iconProps={{
                className: "material-icons-round",
                iconName: "share",
              }}
              iconStyle={{ fontSize: "13px", margin: "2px 0px" }}
              style={{ padding: "4px 10px", buttonHeight: "22px" }}
              disabled={
                reportFormError.flag ||
                disabledAddNewReport ||
                isBucketAssociatedReport(reportFormData)
              }
            />
          </Box>
        </div>
        <div
          className={`input-item-container border-less report-name-container ${
            reportTypeName ? "clone-visible" : "full"
          }`}
        >
          <SigviewTextField
            value={reportFormData.reportName}
            onChange={(newValue) => {
              if (newValue !== " ") {
                // newValue = newValue.trim();
                setReportFormDataSync("reportName", newValue);
              }
            }}
            placeholder="Enter String"
            // error={reportFormError.message.reportName.flag}
            // helperText={reportFormError.message.reportName.message}
            maxWidth="100%"
            height="33px"
            border="none"
            borderRadius="4px"
            // backgroundColor={`${themeColors["primaryColor"]}1A`}
            backgroundColor= "#EEEFF2"
            fontSize="12px"
            readOnly={false}
          />
        </div>
      </div>
      {reportFormError.message.reportName.flag && (
        <div
          style={{
            color: "red",
            fontSize: "10px",
            position: "relative",
            top: "-7px",
            // paddingLeft: "100px",
          }}
        >
          {reportFormError.message.reportName.message}
        </div>
      )}
      {reportFormData.scheduleFlag === "yes" && (
        <>
          <div className="report-manager-form-row">
            <div className="report-manager-form-item-container">
              <div className="label-container">
                <p className="label">{t("Report Status:")}</p>
              </div>
            </div>
            <Box
              style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
              }}
            >
              <SigviewTypography
                style={{
                  height: "max-content",
                  margin: "0px 5px 0px 0px !important",
                  fontWeight: reportFormData.reportStatus !== "active" && "500",
                  color:
                    reportFormData.reportStatus !== "active"
                      ? themeColors["primaryColor"]
                      : themeColors["secondaryColor"],
                }}
              >
                In-active
              </SigviewTypography>
              <div className="input-item-container transparent">
                <SigviewSwitch
                  checked={reportFormData.reportStatus === "active"}
                  customClassName="scheduleReportON-OFF-GA"
                  onChange={(event, checked) => {
                    setReportFormDataSync(
                      "reportStatus",
                      checked ? "active" : "inactive"
                    );
                  }}
                />
              </div>
              <SigviewTypography
                style={{
                  height: "max-content",
                  margin: "0px 0px 0px 5px !important",
                  fontWeight: reportFormData.reportStatus === "active" && "500",
                  color:
                    reportFormData.reportStatus === "active"
                      ? themeColors["primaryColor"]
                      : themeColors["secondaryColor"],
                }}
              >
                Active
              </SigviewTypography>
            </Box>
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "0px 6px",
              justifyContent: "flex-start",
              alignItems: "center",
            }}
          >
            <div className="report-manager-form-row">
              <div className="report-manager-form-item-container">
                <div className="label-container">
                  <p className="label">{t("Trigger Timezone:")}</p>
                </div>
              </div>
              <div className="input-item-container border-less">
                <SigviewSingleSelect
                  value={reportFormData.triggerTimeZone}
                  data={allTimezonesFinal}
                  onChange={(newValue) => {
                    setReportFormDataSync("triggerTimeZone", newValue);
                  }}
                  minWidth="140px"
                  height="33px"
                  fontSize="12px"
                  border="none"
                  // backgroundColor={`${themeColors["primaryColor"]}1A`}
                  backgroundColor= "#EEEFF2"
                  // margin={{ right: "5px" }}
                  theme={user.theme}
                  themeColors={user.themeColors}
                  disabled={false}
                />
              </div>
            </div>
            <div className="report-manager-form-row">
              <div className="report-manager-form-item-container">
                <div className="label-container">
                  <p className="label">{t("Trigger Time:")}</p>
                </div>
              </div>
              <div className="input-item-container border-less">
                <SigviewSingleSelect
                  value={reportFormData.triggerTime}
                  data={triggerTimeArr}
                  onChange={(newValue) => {
                    setReportFormDataSync("triggerTime", newValue);
                  }}
                  minWidth="140px"
                  height="33px"
                  border="none"
                  fontSize="12px"
                  // backgroundColor={`${themeColors["primaryColor"]}1A`}
                  backgroundColor= "#EEEFF2"
                  theme={user.theme}
                  themeColors={user.themeColors}
                  disabled={false}
                />
              </div>
            </div>
            <div className="report-manager-form-row">
              <div className="report-manager-form-item-container">
                <div className="label-container">
                  <p className="label">{t("Frequency:")}</p>
                </div>
              </div>
              <Box
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  gap: "8px 6px",
                }}
              >
                <div className="input-item-container border-less">
                  <SigviewSingleSelect
                    value={reportFormData.triggerType}
                    data={ModifiedTriggerFrequencyArr}
                    onChange={(newValue) => {
                      setReportFormDataSync("triggerType", newValue);
                    }}
                    minWidth="140px"
                    border="none"
                    height="33px"
                    fontSize="12px"
                    // backgroundColor={`${themeColors["primaryColor"]}1A`}
                    backgroundColor= "#EEEFF2"
                    disabled={false}
                  />
                </div>
                {weeklyRadioButtonFlag && (
                  <div className="input-item-container border-less label-less">
                    <SigviewButtonGroup
                      value={reportFormData.triggerDay}
                      data={allWeekdays}
                      onChange={(newValue) => {
                        setReportFormDataSync("triggerDay", newValue);
                      }}
                      style={{
                        border: `1px solid ${themeColors["primaryColor"]}1A`,
                        inactiveBackgroundColor: `${themeColors["primaryColor"]}1A`,
                        height: "33px",
                      }}
                      disabled={false}
                    />
                  </div>
                )}
                {triggerTypeHelperTextFlag && (
                  <div className="input-item-container label-less border-less">
                    <SigviewSimpleText
                      value={triggerTypeHelperText}
                      style={{
                        maxWidth: "max-content",
                        minWidth: "140px",
                        fontSize: "12px",
                        padding: "6px",
                        height: "33px",
                        border: "none",
                        // backgroundColor: `${themeColors["primaryColor"]}1A`,
                        backgroundColor: "#EEEFF2",
                      }}
                    />
                  </div>
                )}
              </Box>
            </div>
          </div>
        </>
      )}
      {reportFormData.scheduleFlag === "no" && (
        <div className="report-manager-form-row" id="reportFormTimeRange">
          <div className="report-manager-form-item-container full">
            <div className="label-container">
              <p className="label">{t("Time Range:")}</p>
            </div>
          </div>
          <div className="input-item-container">
            <TimeFilters
              user={user}
              allData={allData}
              initialTimeFilters={reportFormData.timeFilters}
              onChange={(newValue) => {
                setReportFormDataSync("timeFilters", newValue);
              }}
              googleAnalytics={{ category: "ReportManager" }}
              calendarDaysLimits={calendarDaysLimits}
              settings={timeFiltersSettings}
            />
          </div>
        </div>
      )}
      {reportFormData.scheduleFlag === "yes" && (
        <div className="report-manager-form-row" id="reportFormTimeRange">
          <div className="report-manager-form-item-container">
            <div className="label-container">
              <p className="label">{t("Time Range:")}</p>
            </div>
          </div>
          <Box
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: "8px 6px",
            }}
          >
            <div className="input-item-container border-less">
              {allowTimeRangeForRealTimeCPR && (
                <SigviewSingleSelect
                  value={reportFormData.triggerTimeRangeNumber}
                  data={realTimeCPRTimeRangeScheduledArr}
                  onChange={(newValue) => {
                    setReportFormDataSync("triggerTimeRangeNumber", newValue);
                  }}
                  minWidth="140px"
                  fontSize="12px"
                  border="none"
                  height="33px"
                  // backgroundColor={`${themeColors["primaryColor"]}1A`}
                  backgroundColor= "#EEEFF2"
                  // margin={{ right: "5px" }}
                  theme={user.theme}
                  themeColors={user.themeColors}
                />
              )}
              {allowAllTimeRangeForAllView && (
                <SigviewSingleSelect
                  value={reportFormData.triggerTimeRangeNumber}
                  data={filteredTimeRangeScheduledArr}
                  onChange={(newValue) => {
                    setReportFormDataSync("triggerTimeRangeNumber", newValue);
                  }}
                  minWidth="140px"
                  fontSize="12px"
                  border="none"
                  height="33px"
                  // backgroundColor={`${themeColors["primaryColor"]}1A`}
                  backgroundColor= "#EEEFF2"
                  // margin={{ right: "5px" }}
                  theme={user.theme}
                  themeColors={user.themeColors}
                  disabled={false}
                />
              )}
            </div>
            <div className="input-item-container border-less label-less">
              <SigviewSingleSelect
                value={reportFormData.triggerTimeRangeTimeZone}
                data={allTimezonesFinal}
                onChange={(newValue) => {
                  setReportFormDataSync("triggerTimeRangeTimeZone", newValue);
                }}
                minWidth="140px"
                fontSize="12px"
                border="none"
                height="33px"
                // backgroundColor={`${themeColors["primaryColor"]}1A`}
                backgroundColor= "#EEEFF2"
                // margin={{ right: "5px" }}
                theme={user.theme}
                themeColors={user.themeColors}
                disabled={false}
              />
            </div>
            {isComparisonAvailable && (
              <div className="input-item-container label-less border-less transparent">
                <SigviewCheckboxGroup
                  data={calendarComparisonData}
                  customID="ReportCompare-GA"
                  onChange={(event, id, checked) => {
                    setReportFormDataSync("isComparisonOn", checked);
                  }}
                  style={{
                    height: "33px",
                    // backgroundColor: `${themeColors["primaryColor"]}1A`,
                    backgroundColor: "#EEEFF2",
                    width: "max-content",
                    minWidth: "140px",
                    boxSizing: "border-box",
                  }}
                  disabled={false}
                />
              </div>
            )}
            {isComparisonAvailable &&
              timeRangeScheduledReportOnComparisonTextFlag && (
                <div className="input-item-container label-less border-less">
                  <SigviewSimpleText
                    value={timeRangeScheduledReportOnComparisonText}
                    style={{
                      maxWidth: "max-content",
                      minWidth: "140px",
                      fontSize: "12px",
                      padding: "6px",
                      height: "33px",
                      border: "none",
                      // backgroundColor: `${themeColors["primaryColor"]}1A`,
                      backgroundColor: "#EEEFF2",
                    }}
                  />
                </div>
              )}
          </Box>
        </div>
      )}
      <div className="report-manager-form-row" id="reportFormFilters">
        {((variant || atleastOneFilterSelected) && (
          <div className="report-manager-form-item-container">
            <div className="label-container">
              <p className="label">{t("Filters:")}</p>
            </div>
            <SigviewButton
              variant="containedPrimary"
              title="Add Filters"
              iconFlag={true}
              iconProps={{
                className: "material-icons-round",
                iconName: "add_circle",
              }}
              onClick={handleAddFilters}
              customClassName={`${rootPage}ReportAddFilter-GA`}
              iconStyle={{ fontSize: "13px", margin: "2px 0px" }}
              style={{ padding: "4px 10px", buttonHeight: "22px" }}
            />
          </div>
        )) || <></>}
        <div className="input-item-container transparent filterBox">
          {!atleastOneFilterSelected && (
            <SigviewTypography
              variant="pMedium"
              style={{
                color: `${themeColors["secondaryColor"]}`,
                height: "max-content",
              }}
            >
              No filters added
            </SigviewTypography>
          )}
          {atleastOneFilterSelected && (
            <SelectedFilters
              isReadOnly={false}
              onClick={(row, type) => {
                switch (type) {
                  case "dimension":
                    handleDimFilterClick(row);
                    break;
                  case "metric":
                    handleMetricFilterClick(row);
                    break;
                  default:
                    break;
                }
              }}
              onDelete={(row, type) => {
                switch (type) {
                  case "dimension":
                    handleDimFilterDelete(row);
                    break;
                  case "metric":
                    handleMetricFilterDelete(row);
                    break;
                  default:
                    break;
                }
              }}
              showClearAllButton={true}
              onClearAll={handleClearAllFilters}
              // rowHeader="Applied Filters:"
              selectedDimensionFilters={reportFormData.dimensionFilters}
              selectedMetricFilters={reportFormData.metricFilters}
              style={{ maxHeight: "62px", margin: "3px" }}
              divId="filtersMenuBarRow"
              allData={allData}
              childrenPlacement="start"
            />
          )}
        </div>
      </div>
      {/* <div
        className="report-manager-form-row two-equal"
        id="reportFormMetricDimSelector"
      >
        <div
          className="report-manager-form-item-container"
          style={{ width: "100%" }}
        >
          <div className="label-container" style={{ width: "100%" }}>
            <p className="label">{t("Dimensions & Metrics:")}</p>
          </div>
        </div>
      </div> */}
      <div
        className="report-manager-form-row full"
        id="reportFormMetricDimSelector"
      >
        <div className="report-manager-form-item-containerBigBox">
          <div className="label-container">
            <p className="label">{t("Add Dimensions:")}</p>
          </div>
        </div>
        <div className="input-item-container transparent metrics-dimensions-selector-container">
          <div
            className="metrics-dimensions-selector-row-container"
            style={{ marginInline: "-5px" }}
          >
            <SigviewMultiSelectDnD
              data={dimensionsDndData}
              value={reportFormData.dimensionsList}
              onChange={handleDimensionsListChange}
              title={t("")}
              idAccessor={dimensionIdAccessor}
              labelAccessor={dimensionLabelAccessor}
              variant="horizontal"
              googleAnalytics={{ category: "ReportManager" }}
              user={user}
              renderOptionAutoComplete={renderOptionAutoCompleteDimension}
              disabled={false}
              bgColor="white"
              customPadding="2px 4px"
              borderColor={`1px solid ${themeColors["primaryColor"]}`}
              customClassName={`${rootPage}ReportAddDimension-GA`}
            />
            <div
              className="error-message"
              style={{ ...dimensionListErrorStyle, height: "10px" }}
            >
              <p>{t(reportFormError.message.dimensionsList.message)}</p>
            </div>
          </div>
        </div>
        <div className="report-manager-form-item-containerBigBox">
          <div className="label-container">
            <p className="label">{t("Add Metrics:")}</p>
          </div>
        </div>
        <div
          className="input-item-container transparent label-less metrics-dimensions-selector-container"
          id="reportFormMetricDimSelector"
        >
          <div
            className="metrics-dimensions-selector-row-container"
            style={{ marginInline: "-5px" }}
          >
            <SigviewMultiSelectDnD
              data={metricsDndData}
              value={reportFormData.metricsList}
              onChange={handleMetricsListChange}
              title={t("")}
              idAccessor={metricIdAccessor}
              labelAccessor={metricLabelAccessor}
              variant="horizontal"
              googleAnalytics={{ category: "ReportManager" }}
              user={user}
              renderOptionAutoComplete={renderOptionAutoCompleteMetric}
              disabled={false}
              bgColor="white"
              borderColor={`1px solid ${themeColors["primaryColor"]}`}
              customPadding="2px 4px"
              customClassName={`${rootPage}ReportAddMetric-GA`}
            />
            <div
              className="error-message"
              style={{ ...metricListErrorStyle, height: "10px" }}
            >
              <p>{t(reportFormError.message.metricsList.message)}</p>
            </div>
          </div>
        </div>
      </div>
      {!isFullReportOn && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <div className="report-manager-form-row" id="reportFormOrderBy">
            <div className="report-manager-form-item-container">
              <div className="label-container">
                <p className="label">{t("Data Sorted By:")}</p>
              </div>
            </div>
            <Box
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "flex-start",
                alignItems: "center",
                gap: "8px 6px",
              }}
            >
              {!orderByShowFlag && (
                <div
                  className="input-item-container full border-less"
                  style={{
                    height: "33px",
                    padding: "6px",
                    // backgroundColor: `${themeColors["primaryColor"]}1A`,
                    backgroundColor: "#EEEFF2",
                    border: "none",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <p className="info-title">
                    {t(
                      "Please select at least one dimension or metric to sort"
                    )}
                  </p>
                </div>
              )}
              {orderByShowFlag && (
                <div className="input-item-container border-less">
                  {orderByIdValid && (
                    <SigviewSingleSelect
                      value={reportFormData.orderById}
                      data={allSelectedMetricsDimensions}
                      onChange={(newValue) => {
                        setReportFormDataSync("orderById", newValue);
                      }}
                      minWidth="140px"
                      height="33px"
                      fontSize="12px"
                      // backgroundColor={`${themeColors["primaryColor"]}1A`}
                      backgroundColor= "#EEEFF2"
                      border="none"
                      disabled={false}
                    />
                  )}
                </div>
              )}
              {orderByShowFlag &&
                orderByTypeValid &&
                sortedByTypeArrFiltered.length > 1 && (
                  <div className="report-manager-form-item-container label-less">
                    <div className="input-item-container label-less border-less">
                      <SigviewSingleSelect
                        value={reportFormData.orderByType}
                        data={sortedByTypeArrFiltered}
                        onChange={(newValue) => {
                          setReportFormDataSync("orderByType", newValue);
                        }}
                        minWidth="140px"
                        border="none"
                        height="33px"
                        fontSize="12px"
                        // backgroundColor={`${themeColors["primaryColor"]}1A`}
                        backgroundColor= "#EEEFF2"
                        disabled={false}
                      />
                    </div>
                  </div>
                )}
              {orderByShowFlag && (
                <div className="report-manager-form-item-container label-less">
                  <div className="input-item-container label-less transparent">
                    <SigviewRadioGroup
                      data={ascDescArr}
                      value={reportFormData.orderBy}
                      onChange={(newValue) => {
                        setReportFormDataSync("orderBy", newValue);
                      }}
                      style={{
                        // backgroundColor: `${themeColors["primaryColor"]}1A`,
                        backgroundColor: "#EEEFF2",
                        borderRadius: "4px",
                        fontSize: "12px",
                        padding: "4.5px 10px 4.5px 6px",
                      }}
                      disabled={false}
                    />
                  </div>
                </div>
              )}
            </Box>
          </div>
          <div
            className="report-manager-form-row"
            id="reportFormLanguagePreference"
          >
            {isLanguageSelectionAvailable && (
              <>
                <div className="report-manager-form-item-container">
                  <div className="label-container">
                    <p className="label">{t("Preferred Language:")}</p>
                  </div>
                </div>
                <div className="input-item-container border-less">
                  <SigviewSingleSelect
                    value={reportFormData.preferredLanguage}
                    data={languageOptionsArr}
                    onChange={(newValue) => {
                      setReportFormDataSync("preferredLanguage", newValue);
                    }}
                    minWidth="140px"
                    height="33px"
                    border="none"
                    fontSize="12px"
                    // backgroundColor={`${themeColors["primaryColor"]}1A`}
                    backgroundColor= "#EEEFF2"
                    disabled={false}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <div className="report-manager-form-row" id="reportFormRecordVolume">
        <div className="report-manager-form-item-container">
          <div className="label-container">
            <p className="label">{t("Records Volume:")} </p>
          </div>
        </div>
        <div
          className="input-item-container transparent"
          style={{ marginRight: "-8px" }}
        >
          <SigviewRadioDropdownCombo
            data={filteredReportCountFileType}
            selectedRadio={reportFormData.reportRowCount}
            onRadioChange={(newValue) => {
              setReportFormDataSync("reportRowCount", newValue);
            }}
            onDropdownChange={(newValue) => {
              setReportFormDataSync("reportCountFileType", newValue);
            }}
            secondDropdownData={bucketList}
            disabled={false}
            style={{
              // backgroundColor: `${themeColors["primaryColor"]}1A`,
              backgroundColor: "#EEEFF2",
              rootGap: "8px",
              height: "33px",
              borderRadius: "3px",
            }}
          />
        </div>
      </div>
      {(showEmailComponent || reportFormData.scheduleFlag === "yes") && (
        <div className="report-manager-form-row">
          {variant === "adminReport" && (
            <div className="report-manager-form-item-container">
              <div className="label-container">
                <p className="label">
                  {t("Owner's Email:")}{" "}
                  <span style={{ ...asteriskStyle }}>*</span>
                </p>
              </div>
              <div className="input-item-container transparent">
                {usersData.status === "success" && (
                  <SigviewComboBox
                    data={usersData.result.map((el) => el.email)}
                    value={reportFormData.recipientsList[0].id}
                    onChange={(e, newValue) => {
                      let oldRecipientList = reportFormData.recipientsList;
                      oldRecipientList.shift();

                      oldRecipientList = oldRecipientList.filter(
                        (el) => el.id !== newValue
                      );
                      let modifiedRecipientList = [
                        { id: newValue, name: newValue },
                        ...oldRecipientList,
                      ];

                      setReportFormDataSync(
                        "recipientsList",
                        modifiedRecipientList
                      );
                    }}
                  />
                )}
                {usersData.status === "loading" && <Loader />}
              </div>
            </div>
          )}
          <div className="report-manager-form-item-container">
            <div className="label-container">
              <p className="label">{t("Email Recipients:")}</p>
            </div>
          </div>
          <div className="input-item-container transparent">
            <SigviewInputTags
              data={reportFormData.recipientsList}
              border="none"
              inputValidationFunction={validateEmail}
              onChange={(newValue) => {
                setReportFormDataSync("recipientsList", newValue);
              }}
              variant={variant}
            />
          </div>
        </div>
      )}
    </>
  );
}

NewReportManagerForm.propTypes = {
  user: PropTypes.object,
};

export default NewReportManagerForm;
